import React, { useEffect, useState } from "react";
import Modal from "../Common/Modal";
import "./EditProfileModal.css";
import { User } from "../../Model/User";
import TextBox from "../Common/TextBox";
import IconInfo from "../../Icons/NotificationMessage/Info";
import UserUtil from "../../Utility/UserUtil";
import MtiButton from "../Buttons/MtiButton";
import UserApi from "../../api/UserApi";
import { useDispatch } from "react-redux";
import { sendNotification } from "../../redux/actions/NotificationAction";
import { ENotificationType } from "../../Model/ENotificationType";

const PASSWORD_MISSMATCH = "The new passwords are not match!";
const PASSWORD_WEAK =
  "Password must be 6 to 20 characters which contain at least one numeric digit, one uppercase and one lowercase letter";

type Props = {
  user: User;
  onChangeUser?: (user: User) => void;
  show: boolean;
  onHide: () => void;
};

export default function EditProfileModal({
  show,
  onHide,
  user,
  onChangeUser = () => {},
}: Props) {
  const dispatch = useDispatch();
  const [userState, setUserState] = useState(user);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");

  useEffect(() => {
    setUserState(user);
  }, [user, show]);

  const newPasswordNotMatch = newPassword !== newPasswordConfirm;
  const passwordWeak = !UserUtil.IsUserPasswordStrong(newPassword);
  const somePasswordEmpty = newPassword === "" || newPasswordConfirm === "";

  const isProfileChanges =
    user.name !== userState.name || user.userEmail !== userState.userEmail;
  const isProfileOk = UserUtil.IsValidEmailFormat(
    userState.userEmail ? userState.userEmail : ""
  );
  const isPasswordChanges = !somePasswordEmpty;
  const isPasswordOk = !newPasswordNotMatch && !passwordWeak;

  const onClickSave = () => {
    if (isProfileChanges && isProfileOk) {
      UserApi.updateUser(userState).then(() => {
        dispatch(
          sendNotification(ENotificationType.Success, "Edit Profile Success")
        );
        onChangeUser(userState);
      });
    }

    if (isPasswordChanges && isPasswordOk) {
      UserApi.updateSelfPassword(oldPassword, newPassword).then(() => {
        dispatch(
          sendNotification(ENotificationType.Success, "Edit Profile Success")
        );
      });
    }

    onCloseModal();
  };

  const buttonDisabled = () => {
    if (isProfileChanges && isPasswordChanges) {
      return !(isPasswordOk && isProfileOk);
    }

    return !(
      (isProfileChanges && isProfileOk) ||
      (isPasswordChanges && isPasswordOk)
    );
  };

  const onCloseModal = () => {
    setOldPassword("");
    setNewPassword("");
    setNewPasswordConfirm("");
    onHide();
  };

  const isEmailValidFormat =
    UserUtil.IsValidEmailFormat(userState.userEmail) ||
    userState.userEmail == "";

  return (
    <Modal show={show} onHide={onCloseModal} style={{ padding: "25px 36px" }}>
      <Modal.Header title="Edit Profile" hideButtonEnabled={false} />
      <Modal.Body className="my-profile-container">
        <div className="my-profile-subTitle">Profile</div>
        <div className="my-profile-field">
          <label className="my-profile-label">User name</label>
          <div className="my-profile-input my-profile-text">
            {userState.userName}
          </div>
        </div>
        <div className="my-profile-field">
          <label className="my-profile-label">Name</label>
          <TextBox
            name={"UserName"}
            isInteger={false}
            variant="text"
            value={userState.name??""}
            className="my-profile-input"
            onChange={(value) =>
              setUserState({
                ...userState,
                name: value as string,
              })
            }
            supportAutoFill={false}
          />
        </div>
        <div className="my-profile-field">
          <label className="my-profile-label">Email</label>
          <TextBox
            name={"UserEmail"}
            isInteger={false}
            variant="text"
            value={userState.userEmail??""}
            className={
              isEmailValidFormat
                ? "my-profile-input"
                : "my-profile-input red-border"
            }
            onChange={(value) =>
              setUserState({
                ...userState,
                userEmail: value as string,
              })
            }
          />
        </div>
        {!isEmailValidFormat && (
          <div className="my-profile-not-email">
            <div style={{ display: "flex" }}>
              <IconInfo fill="#C92932" width="10" height="10" />
            </div>
            <p>the email is not valid!</p>
          </div>
        )}
        <hr
          style={{
            marginTop: 30,
            marginBottom: 30,
            border: "1px solid #E0E0E0",
          }}
        />

        <div className="my-profile-subTitle">Password</div>
        <div className="my-profile-field">
          <label className="my-profile-label">Old Password</label>
          <TextBox
            name="oldPassword"
            value={oldPassword}
            onChange={(value) => setOldPassword(value as string)}
            variant="password"
            className="my-profile-input"
            supportAutoFill={false}
          />
        </div>

        <div className="my-profile-field">
          <label className="my-profile-label">New Password</label>
          <TextBox
            name="newPassword1"
            value={newPassword}
            onChange={(value) => setNewPassword(value as string)}
            variant="password"
            className="my-profile-input"
          />
        </div>

        <div className="my-profile-field">
          <label className="my-profile-label">Confirm Password</label>
          <TextBox
            name="newPassword2"
            value={newPasswordConfirm}
            onChange={(value) => setNewPasswordConfirm(value as string)}
            variant="password"
            className="my-profile-input"
          />
        </div>

        {(newPasswordNotMatch || passwordWeak) && !somePasswordEmpty && (
          <div className="my-profile-password-not-equal">
            <div>
              <IconInfo fill="#C92932" width="10" height="10" />
            </div>
            <p>
              {newPasswordNotMatch
                ? PASSWORD_MISSMATCH
                : passwordWeak
                ? PASSWORD_WEAK
                : ""}
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="my-profile-buttons">
        <MtiButton
          size="large"
          style={{ marginLeft: 10 }}
          onClick={onClickSave}
          disabled={buttonDisabled()}
        >
          SAVE
        </MtiButton>
        <MtiButton size="large" variant="secondary" onClick={onCloseModal}>
          CANCEL
        </MtiButton>
      </Modal.Footer>
    </Modal>
  );
}
